import { useMemo } from "react";
import { Search, Plus, LayoutTemplate, Workflow as WorkflowIcon, GitBranch, Clock, TrendingUp } from "lucide-react";
import { useWorkflow } from "@/store/workflowStore";
import { nodeDef, INTEGRATION_META } from "@/data/mockWorkflowNodes";
import { WF_STATUS_META, RiskBadge, Pill } from "@/components/hive/shared";
import { SampleBadge } from "@/components/ui/SampleBadge";
import type { HiveWorkflow } from "@/types";

const FILTERS: { id: string; label: string; test: (w: HiveWorkflow) => boolean }[] = [
  { id: "all", label: "All", test: () => true },
  { id: "active", label: "Active", test: (w) => w.status === "active" },
  { id: "draft", label: "Draft", test: (w) => w.status === "draft" },
  { id: "needs-approval", label: "Needs Approval", test: (w) => w.status === "needs-approval" },
  { id: "broken", label: "Broken", test: (w) => w.status === "broken" },
  { id: "jira", label: "Uses Jira", test: (w) => w.integrations.includes("jira") },
  { id: "slack", label: "Uses Slack", test: (w) => w.integrations.includes("slack") },
  { id: "ares", label: "Uses ARES", test: (w) => w.nodes.some((n) => nodeDef(n.defKey).integration === "ares") },
  { id: "byonc", label: "Uses BYONC", test: (w) => w.nodes.some((n) => nodeDef(n.defKey).integration === "byonc") },
  { id: "high", label: "High Risk", test: (w) => w.risk === "high" || w.risk === "restricted" },
];

export function WorkflowLibrary() {
  const workflows = useWorkflow((s) => s.workflows);
  const selectedId = useWorkflow((s) => s.selectedWorkflowId);
  const selectWorkflow = useWorkflow((s) => s.selectWorkflow);
  const search = useWorkflow((s) => s.search);
  const setSearch = useWorkflow((s) => s.setSearch);
  const filter = useWorkflow((s) => s.libraryFilter);
  const setFilter = useWorkflow((s) => s.setLibraryFilter);
  const newWorkflow = useWorkflow((s) => s.newWorkflow);
  const setTemplatesOpen = useWorkflow((s) => s.setTemplatesOpen);

  const list = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) ?? FILTERS[0];
    const lower = search.trim().toLowerCase();
    return workflows.filter((w) => f.test(w) && (!lower || w.name.toLowerCase().includes(lower) || w.triggerHook.toLowerCase().includes(lower)));
  }, [workflows, filter, search]);

  return (
    <div className="flex h-full w-[280px] shrink-0 flex-col border-r border-app bg-surface/30">
      <div className="border-b border-app p-3">
        <div className="mb-2.5 flex items-center gap-1.5">
          <WorkflowIcon size={15} className="text-rose-500" />
          <span className="text-sm font-semibold text-app">Workflow Library</span>
          <span className="ml-auto text-[11px] text-app-2 tabular-nums">{workflows.length}</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/60 px-2.5 py-1.5">
          <Search size={13} className="text-app-2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search workflows…" className="flex-1 bg-transparent text-xs text-app outline-none placeholder:text-app-2" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button onClick={newWorkflow} className="flex items-center justify-center gap-1 rounded-lg bg-rose-500 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-110"><Plus size={12} /> New</button>
          <button onClick={() => setTemplatesOpen(true)} className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"><LayoutTemplate size={12} /> Templates</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-app px-3 py-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition ${
              filter === f.id ? "border-rose-500/50 bg-rose-500/15 text-rose-400" : "border-app text-app-2 hover:text-app-1"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-1.5 overflow-y-auto no-scrollbar p-2">
        {list.map((w) => (
          <WorkflowCard key={w.id} wf={w} active={w.id === selectedId} onClick={() => selectWorkflow(w.id)} />
        ))}
        {list.length === 0 && <div className="px-3 py-10 text-center text-xs text-app-2">No workflows match.</div>}
      </div>
    </div>
  );
}

function WorkflowCard({ wf, active, onClick }: { wf: HiveWorkflow; active: boolean; onClick: () => void }) {
  const m = WF_STATUS_META[wf.status];
  const integ = [...new Set(wf.integrations)].slice(0, 5);
  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl border p-2.5 text-left transition ${
        active ? "border-rose-500/50 bg-rose-500/[0.07]" : "border-app bg-surface/40 hover:border-app-strong"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-xs font-semibold leading-tight text-app">{wf.name}</span>
          {wf.isSample && <SampleBadge />}
        </span>
        <Pill color={m.color} dot={wf.status === "active"} pulse={wf.status === "active"}>{m.label}</Pill>
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px] text-app-2">
        <GitBranch size={10} /> <span className="truncate">{wf.triggerHook}</span>
      </div>
      <div className="mt-2 flex items-center gap-2.5 text-[10px] text-app-2">
        <span className="flex items-center gap-0.5"><TrendingUp size={10} /> {wf.successRate > 0 ? `${Math.round(wf.successRate * 100)}%` : "—"}</span>
        <span className="flex items-center gap-0.5"><Clock size={10} /> {wf.avgDuration}</span>
        <span>{wf.nodes.length} steps</span>
        <RiskBadge risk={wf.risk} className="ml-auto" />
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <div className="flex items-center gap-0.5">
          {integ.map((id) => {
            const meta = INTEGRATION_META[id];
            return (
              <span key={id} className="grid h-4 w-4 place-items-center rounded text-[8px] font-bold" style={{ background: `${meta?.color ?? "#888"}22`, color: meta?.color ?? "#888" }} title={meta?.label}>
                {meta?.glyph || meta?.label?.[0]}
              </span>
            );
          })}
        </div>
        <span className="ml-auto text-[10px] text-app-2">{wf.owner}</span>
      </div>
    </button>
  );
}
